import {Component, EventEmitter, OnInit, Output, Input} from '@angular/core';
import {RequestService} from "../../../../services/api/v1/request.service";
import {ActivatedRoute, Router} from "@angular/router";
import {CustomMessageService, ResponseHttpSuccess, UtilService} from "library";
import {HttpErrorResponse, HttpStatusCode} from "@angular/common/http";
import {SalesWorkflowSteps, SalesWorkflow} from "projects/store/src/app/enums";
import {OriginMessageError} from "library";
import { environment } from "projects/store/src/environments/environment";
import {PathNavigationService} from "../../../../services/api/v1/path-navigation.service";
import {CategoryRequestService} from "../../../../services/api/v1/category-request.service";
import {forkJoin, Observable, of} from "rxjs";
import {catchError} from "rxjs/operators";


@Component({
  selector: 'app-sales-credit-summary-express',
  templateUrl: './sales-credit-summary-express.component.html',
  styles: []
})
export class SalesCreditSummaryExpressComponent implements OnInit {
  @Input() request: any;
  requestId: string;
  country = environment.global.country;
  flowCode: string;
  categoryList: any[] = [];
  topUpLoan: any = {};
  confirmVisible: boolean = false;
  @Output() nextStepEvent = new EventEmitter();
  @Output() gotoStepEvent = new EventEmitter();

  quotes: any[] = [];
  selectedQuote!: number;


  constructor(private requestService: RequestService,
              private route: ActivatedRoute,
              private router: Router,
              private utilService: UtilService,
              private customMessageService: CustomMessageService,
              private pathNavigationService: PathNavigationService,
              private categoryRequestService: CategoryRequestService) {
    this.requestId = this.route.snapshot.params['request_id'];
    this.flowCode = this.route.snapshot.params['flow_code'];
    this.request = this.requestService.request;
  }

  ngOnInit() {
    this.loadTopUpLoan();
  }

  loadTopUpLoan() {
    this.utilService.enablePageLoading();
    this.requestService.getTopUpExpress(this.requestId).subscribe({
      next: (resp) => {
        if (resp.status === HttpStatusCode.Ok) {
          this.topUpLoan = resp.body.data;
          this.buildQuotesFromTopUpLoan();
        } else {
          this.customMessageService.responseError();
          this.utilService.disablePageLoading();
        }
      },
      error: (error) => {
        this.customMessageService.responseNoticeHandler(error, OriginMessageError.BACKEND);
        this.utilService.disablePageLoading();
      }
    });
  }

  buildQuotesFromTopUpLoan() {
    const options = this.topUpLoan?.new_loan_options ?? [];
    if (!options.length) {
      this.utilService.disablePageLoading();
      return;
    }

    const simulations$: Observable<ResponseHttpSuccess | null>[] = options.map((opt: any) =>
      this.categoryRequestService.simulateCreditForModal(this.requestId, {
        amount: opt.prices.suggested,
        quota_quantity: opt.quota.quantity,
        frecuency: opt.frecuency,
        salary: 0
      }).pipe(catchError(() => of(null)))
    );

    forkJoin(simulations$).subscribe({
      next: (responses: any[]) => {
        this.quotes = options.map((opt: any, index: number) => {
          const sim = responses[index]?.body?.data ?? null;
          return {
            id: index + 1,
            installments: opt.quota.quantity,
            amount: `${this.country.currency}${opt.quota.from.toFixed(2)}`,
            text: `${opt.quota.quantity} cuotas de ${this.country.currency}${opt.quota.from.toFixed(2)}`,
            raw: opt,
            simulation: sim
          };
        });

        const firstPaymentDate = responses
          .map(r => r?.body?.data?.first_payment_date)
          .find(d => !!d);
        if (firstPaymentDate) {
          this.topUpLoan.first_payment_date = firstPaymentDate;
        }

        if (this.quotes.length === 1) {
          this.selectedQuote = this.quotes[0].id;
        }
        this.utilService.disablePageLoading();
      },
      error: (err: HttpErrorResponse) => {
        this.customMessageService.responseNoticeHandler(err, OriginMessageError.BACKEND);
        this.utilService.disablePageLoading();
      }
    });
  }

  onNextStep() {
    const quote = this.quotes.find((q: any) => q.id === this.selectedQuote);
    if (!quote) return;

    const quotesData = {
      ...quote.raw,
      prices: { ...quote.raw.prices, selected: this.topUpLoan?.consolidation?.total_financed },
      quota: { ...quote.raw.quota, selected: quote.raw.quota.from },
      installment: { ...quote.raw.installment, selected: quote.raw.installment.from }
    };

    this.utilService.enablePageLoading();
    this.requestService.update(this.requestId, { quotes: quotesData, product: this.request?.product }).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.NoContent) {
          this.approveRequest();
        } else {
          this.customMessageService.responseNoticeHandler(resp);
          this.utilService.disablePageLoading();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.customMessageService.responseNoticeHandler(err);
        this.utilService.disablePageLoading();
      }
    });
  }

  private approveRequest() {
    this.requestService.approved(this.requestId).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.NoContent) {
          this.nextStepEvent.emit();
        } else {
          this.customMessageService.responseNoticeHandler(resp);
          this.utilService.disablePageLoading();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.customMessageService.responseNoticeHandler(err);
        this.utilService.disablePageLoading();
      }
    });
  }

  onGotoStep(stepCode: string) {
    this.utilService.enablePageLoading();
    this.gotoStepEvent.emit({ flowCode: this.flowCode, stepCode });
  }

  showConfirmReject() {
    this.confirmVisible = true;
  }

  onRejectRequest() {
    this.confirmVisible = false;
    this.onGotoStep(SalesWorkflowSteps['client-identification-express']);
  }

  frecuencyText(frecuency: number) {
    return this.utilService.getFrequencyDescription(frecuency);
  }

  protected readonly SalesWorkflowSteps = SalesWorkflowSteps;
  protected readonly SalesWorkflow = SalesWorkflow;
}
